import { css } from '@emotion/react';
import Image from 'next/image';
import React from 'react';

import { V1Amount, V1DiscountDetail } from '@/api/clients/ohip';
import { MembershipDiscountBadge } from '@/booking-membership/components/MembershipDiscountBadge';
import { MembershipRateContent } from '@/booking-membership/components/MembershipRateContent';
import { getDisloyaltyMembershipPrice } from '@/booking-membership/utils/get-disloyalty-membership-price';
import { Box, Flex } from '@/box';
import { CurrencyPreview } from '@/currency-preview';
import type { IFinancialAmountInstance } from '@/finance';
import { HotelConfiguration, IHotelModelInstance } from '@/hotel';
import { RoomTotal } from '@/hotel/components/room/RoomTotal.component';
import { useTranslation } from '@/i18n';
import { RadioButtonAccordion, RadioGroup } from '@/ui/controls';
import { useTheme } from '@/ui/theme';
import { BodySmall, Caption, Text } from '@/ui/typography';

interface RoomRateSelectorItemProps {
  name: string;
  code: string;
  description?: string | React.ReactNode;
  isAvailable: boolean;
  totalWithTaxes: IFinancialAmountInstance | undefined;
  isTotalInclusiveOfAllTaxes: boolean | undefined;
  netPrice: V1Amount | undefined;
  includedTaxesMessageTranslationKey: HotelConfiguration['legacyTaxes']['includedTaxesMessageTranslationKey'];
  averageNightlyPrice: V1Amount | undefined;

  isSelected: boolean;

  discountDetail: V1DiscountDetail;

  onSelect: () => void;
  hotel: IHotelModelInstance;
  containsFees: boolean;
}

const RoomRateCodeSelectorOption: React.FC<RoomRateSelectorItemProps> = (
  props
) => {
  const { colors, keylines, forms, componentProperties } = useTheme();
  const { t } = useTranslation(['bookingSummary', 'bookingMembership']);

  const membershipPrice = getDisloyaltyMembershipPrice(
    props.hotel.payments.currencyCode
  );

  const isMembershipRate = props.discountDetail.membershipRateCode;
  const isSale = isMembershipRate && props.discountDetail.percentage === 35;

  return (
    <div
      css={css`
        border-top: ${forms.radioGroup?.divider?.border ??
        keylines?.border ??
        `1px solid ${colors.primary300}`};
        &:last-child {
          ${forms.radioButton?.borderBottom &&
          `border-bottom: ${forms.radioButton.borderBottom};`}
        }
      `}
    >
      <RadioButtonAccordion
        onClick={props.onSelect}
        isChecked={props.isSelected}
        isDisabled={!props.isAvailable}
        collapsibleContent={
          membershipPrice && isMembershipRate ? (
            <MembershipRateContent
              pricePerNight={props.averageNightlyPrice}
              membershipPrice={membershipPrice}
              saving={props.discountDetail.amountSaved}
              description={props.description}
              destinationFeeSummary={
                props.containsFees
                  ? props.hotel.destinationFeeSummary
                  : undefined
              }
            />
          ) : (
            <Flex flexDirection="column" gap={16}>
              {props.averageNightlyPrice && (
                <Caption
                  noWrap
                  color="secondary300"
                  css={componentProperties.bedroomCard?.perNightPriceLabel}
                >
                  {t('bookingSummary:labels.room.pricePerNight')}:{' '}
                  <CurrencyPreview amount={props.averageNightlyPrice} />
                </Caption>
              )}
              <Flex flexDirection="column" gap={2}>
                {props.containsFees && props.hotel.destinationFeeSummary && (
                  <Caption color="secondary300">
                    {props.hotel.destinationFeeSummary}
                  </Caption>
                )}
                {props.hotel?.disclaimer && (
                  <Caption color="secondary300">
                    {props.hotel.disclaimer}
                  </Caption>
                )}
              </Flex>
              {props.description && (
                <BodySmall
                  color="secondary300"
                  // TODO: @hardcodedstrings
                  aria-label={`Rate description: ${props.description}`}
                  css={css(forms.radioButton?.description)}
                >
                  {props.description}
                </BodySmall>
              )}
            </Flex>
          )
        }
        testId={props.code}
        value={props.code}
        css={
          isMembershipRate
            ? {
                '&:focus,&:active': {
                  backgroundColor: '#FAFFF6',
                },
              }
            : undefined
        }
      >
        <Flex flexDirection="column" gap="16px">
          {isMembershipRate && (
            <Flex position="relative">
              <Image
                src="/disloyalty/disloyalty-full-text-green.svg"
                alt=""
                width={108}
                height={20}
              />
              {/* image width 108px + 8px gap */}
              <Box css={{ position: 'absolute', left: '116px', top: '-5px' }}>
                <MembershipDiscountBadge
                  text={t(
                    `bookingMembership:roomRate.${
                      isSale ? 'discountSale' : 'discountOff'
                    }`,
                    {
                      discount: `${props.discountDetail.percentage}%`,
                    }
                  )}
                />
              </Box>
            </Flex>
          )}
          <Text
            style="heading6"
            // TODO: @hardcodedstrings
            aria-label={`Selected rate: ${props.name}.`}
            css={{
              ...componentProperties.bedroomCard?.roomRateName,
              [':first-letter']:
                componentProperties.bedroomCard?.roomRateName?.firstLetter,
            }}
          >
            {/* A hack making sure capitalization is applied on a lowercased string */}
            {/* TODO: we should transform to lowercase in all cases and solidify casing per brand via css */}
            {componentProperties.bedroomCard?.roomRateName?.textTransform ===
            'capitalize'
              ? props.name.toLowerCase()
              : props.name}
          </Text>
        </Flex>
        <span>
          {props.netPrice && props.isTotalInclusiveOfAllTaxes !== undefined ? (
            <RoomTotal
              totalWithTaxes={props.totalWithTaxes}
              isTotalInclusiveOfAllTaxes={props.isTotalInclusiveOfAllTaxes}
              price={props.netPrice}
              includedTaxesMessageTranslationKey={
                props.includedTaxesMessageTranslationKey
              }
              priceWithoutDiscount={
                isMembershipRate
                  ? props.discountDetail.amountBeforeDiscountAfterTax
                  : undefined
              }
              taxLabel={props.hotel.legacyTaxes.taxLabel}
            />
          ) : undefined}
        </span>
      </RadioButtonAccordion>
    </div>
  );
};

export interface RoomRateCodeSelectorProps {
  roomRates: Omit<
    RoomRateSelectorItemProps,
    'onSelect' | 'isSelected' | 'hotel'
  >[];

  onSelectRoomRate: (code: string) => void;
  onRequestRoomSelectionReset: () => void;
  selectedRateCode: string | undefined;
  isRateSwitchingLocked: boolean;
  hotel: IHotelModelInstance;
}

export const RoomRateCodeSelector = (props: RoomRateCodeSelectorProps) => {
  const selectedRate = props.roomRates.find(
    (rate) => rate.code === props.selectedRateCode
  );

  return (
    <RadioGroup
      defaultValue={props.selectedRateCode}
      // TODO: @hardcodedstrings
      aria-label="Rates"
    >
      {props.isRateSwitchingLocked && selectedRate ? (
        <RoomRateCodeSelectorOption
          {...selectedRate}
          isSelected={true}
          onSelect={() => {
            // do nothing, we can't switch
          }}
          isAvailable={true}
          hotel={props.hotel}
        />
      ) : (
        <>
          {props.roomRates.map((rate) => (
            <RoomRateCodeSelectorOption
              {...rate}
              key={rate.code}
              isSelected={rate.code === props.selectedRateCode}
              onSelect={() => props.onSelectRoomRate(rate.code)}
              hotel={props.hotel}
            />
          ))}
        </>
      )}
    </RadioGroup>
  );
};
